import { Lightning, Colors } from '@lightningjs/sdk';
import InputField from 'components/common/input/InputField';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { constants } from 'aliases';
import * as yup from 'yup';
import InputFieldError from './InputFieldError';
import { LoginRadiusError } from 'types/loginRadius';
import { isFieldLevelValidationError } from 'support/loginRadiusUtils';
import TextLabel from '../TextLabel';

export interface LoginInputFieldTemplateSpec
  extends Lightning.Component.TemplateSpec {
  toggleCursor?: boolean;
  errorType?: string;
  errorMessage?: string;
  inputField: InputField;
  isKeyboardActive?: () => boolean;
  showChangeText: boolean;
  schema?: yup.Schema<unknown>;
  placeholderText: string;

  LoginInput: {
    Input: {
      Label: typeof TextLabel;
      InputField: typeof InputField;
    };
    ChangeToggleLayout: {
      ChangeToggle: typeof TextLabel;
      Underline: Lightning.textures.RectangleTexture;
    };
  };
  InputError: typeof InputFieldError;
}

const DEFAULT_WIDTH = 684;
const DEFAULT_HEIGHT = 76;
const DEFAULT_PADDING_X = 8;
const DEFAULT_PADDING_Y = 4;

const BACKGROUND_RADIUS = 8;
const BACKGROUND_STROKE = 4;
const BACKGROUND_RADIUS_WITH_ERROR = [8, 8, 0, 0];

const INPUT_FONT_SIZE = 22;
const INPUT_LINE_HEIGHT = 28;
const INPUT_FIELD_PADDING = 2;
const INPUT_FIELD_WIDTH = 590;

const CHANGE_TOGGLE_FONT_SIZE = 18;
const CHANGE_TOGGLE_HEIGHT = 29;
const CHANGE_TOGGLE_WIDTH = 62;
const CHANGE_TOGGLE_LAYOUT_WIDTH = 94;
const CHANGE_TOGGLE_PADDING_TOP = 30;
const CHANGE_TOGGLE_UNDERLINE_MARGIN_TOP = -45;
const CHANGE_TOGGLE_Y_OFFSET = 21;

const CURSOR_WIDTH = 3;
const CURSOR_HEIGHT = 45;
const CURSOR_Y = -8;

export default class LoginInputField<
    TemplateSpec extends LoginInputFieldTemplateSpec = LoginInputFieldTemplateSpec,
  >
  extends HoverableComponent<TemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LoginInputFieldTemplateSpec>
{
  // Keep an internal version of cursor toggled so we don't have to rely on InputField's
  // value since the cursor can blink while the input is unfocused
  private _cursorToggled: LoginInputFieldTemplateSpec['toggleCursor'];
  private _LoginInput = (this as LoginInputField).getByRef('LoginInput')!;
  private _Input = this._LoginInput.getByRef('Input')!;
  private _InputField = this._Input.getByRef('InputField')!;
  private _ChangeToggleLayout =
    this._LoginInput.getByRef('ChangeToggleLayout')!;
  private _InputError = (this as LoginInputField).getByRef('InputError')!;
  private _isKeyboardActive?: () => boolean;
  private _errorType = '';
  private _errorMessage = '';

  private _backgroundColor = {
    focused: Colors('labelText').get(),
    unfocused: Colors('inputFieldBackground').get(),
  };

  private _fontColor = {
    focused: Colors('inputFieldTextSelected').get(),
    unfocused: Colors('labelText').get(),
  };

  private _borderColor = {
    focused: Colors('focusRed').get(),
    selected: Colors('labelText').get(),
    unfocused: Colors('inputFieldBackground').get(),
  };

  get title() {
    return translate('ttsPrompts.textInput');
  }

  get inputField() {
    return this._InputField;
  }

  get input() {
    return this._InputField._input;
  }

  set input(value: string) {
    this._InputField._input = value;
  }

  override get w() {
    return this._InputField.w;
  }

  get description() {
    return this._InputField.description;
  }

  set description(value: string) {
    this._InputField.description = value;
  }

  set isKeyboardActive(callback: () => boolean) {
    this._isKeyboardActive = callback;
  }

  set showChangeText(value: boolean) {
    this._ChangeToggleLayout?.patch({ visible: value });
  }

  set placeholderText(value: string) {
    this._InputField.patch({ description: value });
  }

  set schema(value: yup.Schema<unknown> | undefined) {
    this._InputField.schema = value;
  }

  set toggleCursor(toggle: LoginInputFieldTemplateSpec['toggleCursor']) {
    if (this._cursorToggled === toggle) return;
    this._cursorToggled = toggle;
    this._InputField.toggleCursor(toggle);
  }

  set backgroundColor(value: { focused?: number; unfocused?: number }) {
    this._backgroundColor = {
      focused: value.focused ?? this._backgroundColor.focused,
      unfocused: value.unfocused ?? this._backgroundColor.unfocused,
    };

    if (this.hasFocus()) {
      this._LoginInput.patch({
        color: this._backgroundColor.focused,
        alpha: 1,
      });
    } else {
      this._LoginInput.patch({
        color: this._backgroundColor.unfocused,
        alpha: 0.2,
      });
    }
  }

  set fontColor(value: { focused?: number; unfocused?: number }) {
    this._fontColor = {
      focused: value.focused ?? this._fontColor.focused,
      unfocused: value.unfocused ?? this._fontColor.unfocused,
    };

    this._InputField.patch({
      text: {
        textColor: this._fontColor[this.hasFocus() ? 'focused' : 'unfocused'],
      },
    });
  }

  set borderColor(value: {
    focused?: number;
    selected?: number;
    unfocused?: number;
  }) {
    this._borderColor = {
      focused: value.focused ?? this._borderColor.focused,
      selected: value.selected ?? this._borderColor.selected,
      unfocused: value.unfocused ?? this._borderColor.unfocused,
    };

    this._LoginInput.patch({
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BACKGROUND_RADIUS,
        stroke: BACKGROUND_STROKE,
        strokeColor:
          this._borderColor[this.hasFocus() ? 'focused' : 'unfocused'],
      },
    });
  }

  get errorMessage() {
    return this._errorMessage;
  }

  set errorMessage(value: string) {
    this._errorMessage = value;
  }

  get errorType() {
    return this._errorType;
  }

  set errorType(value: string) {
    this._errorType = value;
  }

  static override _template(): Lightning.Component.Template<LoginInputFieldTemplateSpec> {
    return {
      flex: {
        direction: 'column',
      },
      LoginInput: {
        rect: true,
        w: DEFAULT_WIDTH,
        h: DEFAULT_HEIGHT,
        color: Colors('inputFieldBackground').get(),
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BACKGROUND_RADIUS,
          stroke: BACKGROUND_STROKE,
        },
        flex: {
          direction: 'row',
          paddingTop: DEFAULT_PADDING_Y,
          paddingBottom: DEFAULT_PADDING_Y,
          alignItems: 'center',
        },
        Input: {
          flex: {
            direction: 'row',
            alignItems: 'center',
            paddingTop: DEFAULT_PADDING_Y,
            paddingLeft: DEFAULT_PADDING_X,
          },
          w: (w: number) => this.getInputWidth(w),
          h: DEFAULT_HEIGHT,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: BACKGROUND_RADIUS,
            stroke: BACKGROUND_STROKE,
            strokeColor: Colors('transparent').get(),
          },
          InputField: {
            type: InputField,
            w: this.getInputWidth(INPUT_FIELD_WIDTH),
            inputText: {
              fontSize: INPUT_FONT_SIZE,
              textColor: Colors('labelText').get(),
              lineHeight: INPUT_LINE_HEIGHT,
              paddingLeft: INPUT_FIELD_PADDING,
              paddingRight: INPUT_FIELD_PADDING,
            },
            cursor: {
              w: CURSOR_WIDTH,
              h: CURSOR_HEIGHT,
              y: CURSOR_Y,
              color: Colors('labelText').get(),
            },
          },
        },
        ChangeToggleLayout: {
          flex: {
            direction: 'column',
            paddingTop: CHANGE_TOGGLE_PADDING_TOP,
            alignItems: 'center',
          },
          x: -CHANGE_TOGGLE_WIDTH - DEFAULT_PADDING_X * 2,
          w: CHANGE_TOGGLE_WIDTH,
          h: CHANGE_TOGGLE_HEIGHT,
          ChangeToggle: {
            type: TextLabel,
            w: CHANGE_TOGGLE_WIDTH,
            h: CHANGE_TOGGLE_HEIGHT,
            mountX: 0,
            mountY: 0.5,
            text: {
              text: translate('account.login.toggleInputLabel'),
              fontSize: CHANGE_TOGGLE_FONT_SIZE,
              textColor: Colors('labelText').get(),
              wordWrapWidth: CHANGE_TOGGLE_WIDTH,
              lineHeight: 50,
            },
          },
          Underline: {
            flexItem: { marginTop: CHANGE_TOGGLE_UNDERLINE_MARGIN_TOP },
            mountX: 0,
            mountY: 0.5,
            y: CHANGE_TOGGLE_Y_OFFSET,
            w: CHANGE_TOGGLE_WIDTH,
            h: 2,
            rect: true,
            alpha: 1,
            color: Colors('labelText').get(),
          },
        },
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
      },
      InputError: {
        type: InputFieldError,
      },
    };
  }

  static getInputWidth(width?: number): number {
    return width && width > DEFAULT_WIDTH
      ? width - CHANGE_TOGGLE_LAYOUT_WIDTH
      : DEFAULT_WIDTH;
  }

  override _setup() {
    super._setup();
    this.update();
    this._setState('Input');
  }

  override _active(): void {
    this._InputField._cursorIndex = this.input.length;
  }

  override _focus() {
    this.update();
    this.checkValid();
  }

  override async _unfocus() {
    this.update();
    if (this.input !== '') this.checkValid();
  }

  private update() {
    this.updateAlpha();

    const cursorVisible = this._cursorToggled;
    const colorKey = this.hasFocus() || cursorVisible ? 'focused' : 'unfocused';

    const cursorColor =
      this._fontColor[cursorVisible ? 'focused' : 'unfocused'];
    const inputTextColor = this._fontColor[colorKey];
    const inputText = { textColor: inputTextColor };

    const color = this._backgroundColor[colorKey];
    const strokeColor = this.selectColor(
      this._borderColor.focused,
      this._borderColor.unfocused,
      this._borderColor.selected,
    );

    this._InputField.patch({ inputText, cursor: { color: cursorColor } });

    this._LoginInput.patch({
      color,
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BACKGROUND_RADIUS,
        stroke: BACKGROUND_STROKE,
        strokeColor,
      },
    });
  }

  private updateAlpha() {
    const cursorVisible = this._cursorToggled;
    const alpha = this.hasFocus() || cursorVisible ? 1 : 0.5;
    const alphaToggle = this.hasFocus() || cursorVisible ? 0 : 1;

    this._Input.patch({ alpha });
    this._ChangeToggleLayout.patch({
      alpha: alphaToggle,
    });
  }

  private selectColor(
    focusedColor: number,
    unfocusedColor: number,
    selectedColor?: number,
  ) {
    const cursorVisible = this._cursorToggled;
    const defaultColor =
      this.hasFocus() || cursorVisible ? focusedColor : unfocusedColor;

    if (!selectedColor) return defaultColor;

    return cursorVisible ? selectedColor : defaultColor;
  }

  async validate(input?: string) {
    return await this._InputField.validate(input ?? this.input);
  }

  async checkValid() {
    const validation = await this.validate(this.input);
    const showError = !!(
      validation.error &&
      !this.hasFocus() &&
      !(this._isKeyboardActive?.() ?? false)
    );

    this._LoginInput.patch({
      shader: {
        radius: showError ? BACKGROUND_RADIUS_WITH_ERROR : BACKGROUND_RADIUS,
      },
    });

    this._InputError.showError(showError);

    if (!showError) return true;

    this._InputError.errorMessage = this.errorMessage;
    this._InputError.announce(this._errorType, this.errorMessage);

    return false;
  }

  override _handleEnter() {
    this.signal('$loginInputSelected', this);
  }

  showFieldLevelError(error: LoginRadiusError) {
    const errorMessage = translate(
      isFieldLevelValidationError(error)
        ? 'account.login.wrongPasswordError'
        : 'account.login.invalidPasswordError',
    );

    this._LoginInput.patch({
      shader: {
        radius: BACKGROUND_RADIUS_WITH_ERROR,
      },
    });

    this._InputError.showError(true);

    this._InputError.errorMessage = errorMessage;
    this._InputError.announce(this._errorType, errorMessage);
  }
}
